import { useState, useEffect, useRef, useCallback } from "react";
import {
  ArrowUpRight, Cloud as CloudIcon, Highlighter, Pen, Type as TypeIcon,
  Circle as CircleIcon, Square as SquareIcon, MessageSquare, Trash2, X, Eraser, Undo2,
  AlertTriangle, ShieldAlert, RefreshCw,
} from "lucide-react";
import React from "react";
import {
  listMarkups, createMarkup, deleteMarkup as apiDeleteMarkup,
} from "@/lib/markupsApi";
import { enqueue as offlineEnqueue, start as offlineStart, dropEntry } from "@/lib/offlineQueueV2";
import { confirm } from "@/lib/confirm";

// Boot the offline queue flusher once.
if (typeof window !== "undefined" && !window.__fop_q_started) {
  window.__fop_q_started = true;
  offlineStart();
}

/**
 * MarkupTool — Iter 73 Phase 1 — PERSISTENT operational field markups.
 *
 * Backed by `/api/projects/{project_id}/sheets/{sheet_id}/markups`.
 * Tenant-scoped, audit-logged, survives refresh + sheet switch.
 *
 * Tools: arrow · cloud · highlight · freehand · text · circle · rectangle ·
 * callout. (`cloud` maps to backend type `polyline` for now — same geometry.)
 *
 * Each markup is { id, type, color, points: [{nx, ny}, ...], text?, label? }
 */

const TOOLS = [
  { id: "arrow", icon: ArrowUpRight, label: "Arrow", taps: 2 },
  { id: "rectangle", icon: SquareIcon, label: "Rect", taps: 2 },
  { id: "circle", icon: CircleIcon, label: "Circle", taps: 2 },
  { id: "cloud", icon: CloudIcon, label: "Cloud", taps: 2 },
  { id: "highlight", icon: Highlighter, label: "Highlight", taps: 2 },
  { id: "freehand", icon: Pen, label: "Freehand", taps: 0 }, // drag
  { id: "text", icon: TypeIcon, label: "Text", taps: 1 },
  { id: "callout", icon: MessageSquare, label: "Callout", taps: 2 },
];
const ORANGE = "var(--fop-primary)";
// Iter EXP-4 — Foreman palette (Phase 4 directive).
// Six named field-recognizable colors. No color pickers. No hex. The
// names are what a superintendent says on the radio: "Mark it Safety
// Red." Each swatch is glove-sized (28×28 hit box, 22px chip).
const COLORS = [
  { hex: "#EF4444", name: "Safety Red",      use: "danger / clash" },
  { hex: "#3B82F6", name: "Utility Blue",    use: "underground util" },
  { hex: "#10B981", name: "Grade Green",     use: "approved / good" },
  { hex: "#F97316", name: "Concrete Orange", use: "pour / form" },
  { hex: "#06B6D4", name: "Survey Cyan",     use: "control / station" },
  { hex: "#A855F7", name: "Review Purple",   use: "QA / PM review" },
];

// Map UI tool ids → backend markup `type` (cloud is rendered client-side
// from a 2-point bounding box; backend stores it as polyline for now).
function uiToBackendType(uiType) {
  if (uiType === "cloud") return "polyline";
  return uiType;
}

/* Hook: own all markup state. Called once from SheetViewer. */
export function useMarkups(sheetId, projectId) {
  const [markups, setMarkups] = useState([]);
  const [tool, setTool] = useState(null); // null | tool id
  // Iter EXP-4 — default to Safety Red (the foreman palette's
  // most-recognized field color). Stored hex matches COLORS[0].hex.
  const [color, setColor] = useState("#EF4444");
  const [loading, setLoading] = useState(false);
  // R-06 (independent recheck, 2026-10-01) — null | "stale" | "forbidden".
  // Surfaces WHY the list might not reflect reality instead of letting
  // a silent [] or a quietly-reused cached row look like a clean load.
  const [readState, setReadState] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const retry = useCallback(() => setReloadTick((t) => t + 1), []);

  // Load persisted markups whenever sheet changes (or retry() is called).
  useEffect(() => {
    let alive = true;
    let degradedKind = null;
    const onDegraded = (ev) => {
      const d = ev.detail || {};
      if (d.resource === "markups" && String(d.projectId) === String(projectId) && String(d.sheetId) === String(sheetId)) {
        degradedKind = d.kind;
      }
    };
    window.addEventListener("forgedops:read-degraded", onDegraded);
    setTool(null);
    if (!sheetId || !projectId) {
      setMarkups([]);
      setReadState(null);
      return () => window.removeEventListener("forgedops:read-degraded", onDegraded);
    }
    setLoading(true);
    listMarkups(projectId, sheetId).then((rows) => {
      if (alive) {
        // Convert backend rows → UI shape. Iter 178.9: polylines with a
        // `label === "__ui:cloud"` sentinel are cloud markups persisted as
        // polylines server-side; restore their UI identity here so the
        // renderer draws the scalloped cloud shape.
        setMarkups(rows.map((r) => {
          if (r.type === "polyline" && r.label === "__ui:cloud") {
            return { ...r, type: "cloud", label: null };
          }
          return { ...r };
        }));
        setLoading(false);
        setReadState(degradedKind);
      }
    });
    return () => { alive = false; window.removeEventListener("forgedops:read-degraded", onDegraded); };
  }, [sheetId, projectId, reloadTick]);

  // Persist on add. Optimistic — show the markup immediately, then reconcile
  // with the server-returned row (which carries the real id + audit identity).
  // Iter 74 Phase 3 — when offline, route the create through the offline queue
  // and keep the optimistic row visible (it will reconcile on next reload).
  const add = useCallback(async (m) => {
    if (!projectId || !sheetId) return;
    const tmpId = `tmp_${Date.now()}`;
    // Iter 178.9 — Cloud persistence fix. Backend stores clouds as
    // `polyline`; on reload we can't tell them apart from real polylines.
    // Persist the UI intent via the `label` field with a sentinel so the
    // list-markups mapper (L76-82) can restore the cloud identity.
    const isCloud = m.type === "cloud";
    const body = {
      type: uiToBackendType(m.type),
      points: m.points,
      color: m.color || "#ff6a1a",
      stroke_width: m.stroke_width || 2.0,
      text: m.text || null,
      label: m.label || (isCloud ? "__ui:cloud" : null),
    };
    const optimistic = { id: tmpId, ...m, _pending: true };
    setMarkups((prev) => [...prev, optimistic]);
    const isOnline = (typeof navigator === "undefined") ? true : navigator.onLine !== false;
    if (!isOnline) {
      // Queue and leave optimistic in place — UI is offline-aware via
      // status bar. GH-19 (real-plan certification, 2026-10-01) —
      // retain the queue operation's OWN id on the optimistic row so
      // undo/remove/clear can find and cancel this exact queued create
      // later; previously nothing recorded this link, so "undo" could
      // only ever clear the local row, never the still-queued create
      // (reconnecting resurrected the markup the operator had just
      // undone).
      const queueOpId = offlineEnqueue("markup.create", { projectId, sheetId, body });
      setMarkups((prev) => prev.map((x) => (x.id === tmpId ? { ...x, _queueOpId: queueOpId } : x)));
      return;
    }
    try {
      const saved = await createMarkup(projectId, sheetId, body);
      // Preserve the UI-side type so the SVG renderer keeps drawing the
      // scalloped cloud shape after the server response overwrites the row.
      setMarkups((prev) => prev.map((x) => (x.id === tmpId ? { ...saved, type: m.type } : x)));
    } catch (e) {
      // Network error → queue + keep optimistic; non-network error → roll
      // back AND surface the backend's rejection reason so the operator
      // isn't left wondering why their placement vanished. Iter 179 —
      // silent-failure elimination.
      const code = e?.response?.status;
      if (!code) {
        const queueOpId = offlineEnqueue("markup.create", { projectId, sheetId, body });
        setMarkups((prev) => prev.map((x) => (x.id === tmpId ? { ...x, _queueOpId: queueOpId } : x)));
      } else {
        setMarkups((prev) => prev.filter((x) => x.id !== tmpId));
        confirm.saveFailed(
          `Couldn't add ${m.type} markup`,
          {
            error: e,
            retry: () => add(m),
          },
        );
      }
    }
  }, [projectId, sheetId]);

  // GH-19 (real-plan certification, 2026-10-01) — cancel a still-queued
  // `markup.create` for an optimistic row that never reached the
  // server. Returns true if the cancellation is certain (the queued op
  // was found and removed before it could ever be sent). Returns false
  // when the op is no longer in the queue — it may have already
  // flushed in the brief window between the tap and this call — in
  // which case we do NOT know the resulting server id and cannot send
  // a delete for it; the caller must surface that honestly rather than
  // silently assuming it's gone.
  const cancelQueuedCreate = useCallback((target) => {
    if (!target?._pending) return true; // nothing queued to cancel
    if (!target._queueOpId) return false; // pre-fix row, no identity to cancel by
    const removed = dropEntry(target._queueOpId);
    return !!removed;
  }, []);

  const remove = useCallback(async (id) => {
    const target = markups.find((m) => m.id === id);
    setMarkups((prev) => prev.filter((m) => m.id !== id));
    if (target?._pending) {
      if (!cancelQueuedCreate(target)) {
        confirm.saveFailed(`Couldn't confirm the ${target.type} markup was cancelled`, {
          recovery: "It may have already synced — check the markup list after reconnecting.",
        });
      }
      return;
    }
    if (target) {
      const isOnline = (typeof navigator === "undefined") ? true : navigator.onLine !== false;
      if (!isOnline) {
        offlineEnqueue("markup.delete", { markupId: id });
        return;
      }
      try { await apiDeleteMarkup(id); }
      catch (e) {
        if (!e?.response?.status) offlineEnqueue("markup.delete", { markupId: id });
      }
    }
  }, [markups, cancelQueuedCreate]);

  // Local-only convenience — clears in-memory + soft-deletes each persisted.
  const clearAll = useCallback(async () => {
    const pending = markups.filter((m) => m._pending);
    const persisted = markups.filter((m) => !m._pending);
    setMarkups([]);
    let uncancelled = 0;
    for (const m of pending) {
      if (!cancelQueuedCreate(m)) uncancelled += 1;
    }
    let failed = 0;
    for (const m of persisted) {
      try { await apiDeleteMarkup(m.id); }
      catch (e) {
        // GH-19 — a delete that fails for a real (non-network) reason
        // must not be silently ignored: the markup still exists on the
        // server even though "Clear" just wiped it from the screen.
        // Network failures fall back to the same queued-delete path
        // `remove()` already uses so reconnecting finishes the job.
        if (!e?.response?.status) offlineEnqueue("markup.delete", { markupId: m.id });
        else failed += 1;
      }
    }
    if (failed > 0 || uncancelled > 0) {
      confirm.saveFailed(
        `${failed + uncancelled} item${failed + uncancelled === 1 ? "" : "s"} may not have cleared`,
        { recovery: "Check the markup list after reconnecting — some may still be there." },
      );
    }
  }, [markups, cancelQueuedCreate]);

  // Iter EXP-3 — Undo the most-recent markup.
  // The operator has been keyboard-only on Cmd+Z; the iPad has no
  // keyboard so undo needs a visible button. Pulls the latest-by-
  // creation markup and deletes it through the same path as remove().
  const undoLast = useCallback(async () => {
    if (markups.length === 0) return;
    // Prefer the most-recent local push: array is append-only.
    const last = markups[markups.length - 1];
    if (!last) return;
    setMarkups((prev) => prev.filter((m) => m.id !== last.id));
    if (last._pending) {
      if (!cancelQueuedCreate(last)) {
        confirm.saveFailed(`Couldn't confirm the ${last.type} markup was undone`, {
          recovery: "It may have already synced — check the markup list after reconnecting.",
        });
      }
      return;
    }
    const isOnline = (typeof navigator === "undefined") ? true : navigator.onLine !== false;
    if (!isOnline) {
      offlineEnqueue("markup.delete", { markupId: last.id });
      return;
    }
    try { await apiDeleteMarkup(last.id); }
    catch (e) {
      if (!e?.response?.status) offlineEnqueue("markup.delete", { markupId: last.id });
    }
  }, [markups, cancelQueuedCreate]);

  return { markups, tool, color, loading, readState, retry, setTool, setColor, add, remove, clearAll, undoLast };
}

/* ------------------------------------------------------------------ */
/* MarkupSvgLayer — renders all markups + live in-progress shape       */
/* ------------------------------------------------------------------ */
export function MarkupSvgLayer({ markups, imgDims, viewScale, inProgress }) {
  const W = imgDims?.w || 1;
  const H = imgDims?.h || 1;
  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      data-testid="markup-svg"
    >
      {markups.map((m) => <Markup key={m.id} m={m} W={W} H={H} vs={viewScale} />)}
      {inProgress && <Markup m={inProgress} W={W} H={H} vs={viewScale} preview />}
    </svg>
  );
}

function Markup({ m, W, H, vs, preview }) {
  const stroke = m.color || ORANGE;
  // Iter EXP-1 (Phase 1B) — markup visuals must read at arm's length on
  // a sunlight-glared iPad. Bluebeam ships ~3.5–5px strokes; HCSS ships
  // similar. We were at 1.8 / vs (1.8 CSS pixels at 1x zoom). Bumped
  // to 4.5px with a soft shadow filter for depth.
  const sw = 4.5 / vs;
  const opacity = preview ? 0.7 : 1;
  const dropShadow = preview ? undefined : "drop-shadow(0 1px 2px rgba(0,0,0,0.35))";
  const p0 = m.points?.[0];
  const p1 = m.points?.[1];
  // IP-001/IP-002 — inert DOM instrumentation. Anchor is first point; type
  // resolves to markup-{kind}. Non-persisted preview shapes skip the id
  // attribute but still expose the overlay markers so the projection probe
  // can enumerate live in-progress renders too.
  const overlayProps = p0 ? {
    "data-viewer-overlay": "1",
    "data-overlay-type": `markup-${m.type}`,
    "data-overlay-id": m.id || "",
    "data-overlay-anchor-nx": p0.nx,
    "data-overlay-anchor-ny": p0.ny,
  } : {};
  const inner = renderMarkupGeometry({ m, stroke, sw, opacity, dropShadow, p0, p1, W, H, vs });
  if (inner == null) return null;
  return <g {...overlayProps}>{inner}</g>;
}

function renderMarkupGeometry({ m, stroke, sw, opacity, dropShadow, p0, p1, W, H, vs }) {
  switch (m.type) {
    case "arrow": {
      if (!p0 || !p1) return null;
      const x1 = p0.nx * W, y1 = p0.ny * H, x2 = p1.nx * W, y2 = p1.ny * H;
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const head = 28 / vs;
      const a1x = x2 - head * Math.cos(angle - Math.PI / 7);
      const a1y = y2 - head * Math.sin(angle - Math.PI / 7);
      const a2x = x2 - head * Math.cos(angle + Math.PI / 7);
      const a2y = y2 - head * Math.sin(angle + Math.PI / 7);
      return (
        <g opacity={opacity} style={{ filter: dropShadow }}>
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth={sw} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          <polygon points={`${x2},${y2} ${a1x},${a1y} ${a2x},${a2y}`} fill={stroke} stroke={stroke} strokeWidth={sw * 0.5} strokeLinejoin="round" />
        </g>
      );
    }
    case "rectangle": {
      if (!p0 || !p1) return null;
      const x = Math.min(p0.nx, p1.nx) * W, y = Math.min(p0.ny, p1.ny) * H;
      const w = Math.abs(p1.nx - p0.nx) * W, h = Math.abs(p1.ny - p0.ny) * H;
      return <rect x={x} y={y} width={w} height={h} fill="none" stroke={stroke} strokeWidth={sw} opacity={opacity} vectorEffect="non-scaling-stroke" style={{ filter: dropShadow }} />;
    }
    case "circle": {
      if (!p0 || !p1) return null;
      const cx = p0.nx * W, cy = p0.ny * H;
      const rr = Math.hypot((p1.nx - p0.nx) * W, (p1.ny - p0.ny) * H);
      return <circle cx={cx} cy={cy} r={rr} fill="none" stroke={stroke} strokeWidth={sw} opacity={opacity} vectorEffect="non-scaling-stroke" style={{ filter: dropShadow }} />;
    }
    case "highlight": {
      if (!p0 || !p1) return null;
      const x = Math.min(p0.nx, p1.nx) * W, y = Math.min(p0.ny, p1.ny) * H;
      const w = Math.abs(p1.nx - p0.nx) * W, h = Math.abs(p1.ny - p0.ny) * H;
      // LAST-CHANCE RESCUE — highlight reads like a real highlighter
      // pass: lower opacity, mix-blend-mode multiply so the underlying
      // plan content shows through (not a flat rectangle obscuring it).
      return <rect x={x} y={y} width={w} height={h} fill={stroke} fillOpacity={0.35} opacity={opacity} style={{ mixBlendMode: "multiply" }} />;
    }
    case "cloud": {
      if (!p0 || !p1) return null;
      const x = Math.min(p0.nx, p1.nx) * W, y = Math.min(p0.ny, p1.ny) * H;
      const w = Math.abs(p1.nx - p0.nx) * W, h = Math.abs(p1.ny - p0.ny) * H;
      const r = Math.min(w, h) / 8 || 6;
      // Build scalloped path along the rectangle perimeter.
      const arcs = (len, axisX, axisY, startX, startY) => {
        const n = Math.max(2, Math.round(len / (r * 2)));
        const step = len / n;
        const path = [];
        for (let i = 0; i < n; i++) {
          const sx = startX + axisX * (step * i + step / 2);
          const sy = startY + axisY * (step * i + step / 2);
          path.push(`A ${step / 2} ${step / 2} 0 0 1 ${startX + axisX * step * (i + 1)} ${startY + axisY * step * (i + 1)}`);
        }
        return path.join(" ");
      };
      const d = `M ${x} ${y} ${arcs(w, 1, 0, x, y)} ${arcs(h, 0, 1, x + w, y)} ${arcs(w, -1, 0, x + w, y + h)} ${arcs(h, 0, -1, x, y + h)} Z`;
      return <path d={d} fill="none" stroke={stroke} strokeWidth={sw} opacity={opacity} vectorEffect="non-scaling-stroke" style={{ filter: dropShadow }} />;
    }
    case "freehand": {
      const pts = m.points || [];
      if (pts.length < 2) return null;
      const d = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.nx * W} ${p.ny * H}`).join(" ");
      return <path d={d} fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" opacity={opacity} vectorEffect="non-scaling-stroke" style={{ filter: dropShadow }} />;
    }
    case "text": {
      if (!p0) return null;
      // LAST-CHANCE RESCUE — text markup is a plan annotation, not a UI
      // pill. Halo-text (white stroke + colored fill via paint-order) reads
      // crisply on any background — white paper, dark satellite, mixed PDF —
      // without the ugly black warning chip the operator called out.
      const textStr = m.text || "";
      return (
        <g opacity={opacity}>
          <text
            x={p0.nx * W}
            y={p0.ny * H}
            fontFamily="ui-monospace,Menlo,monospace"
            fontSize={18 / vs}
            fontWeight="800"
            fill={stroke}
            stroke="#ffffff"
            strokeWidth={5 / vs}
            strokeLinejoin="round"
            paintOrder="stroke"
            style={{ paintOrder: "stroke" }}
          >
            {textStr}
          </text>
        </g>
      );
    }
    case "callout": {
      if (!p0 || !p1) return null;
      const ax = p0.nx * W, ay = p0.ny * H;
      const bx = p1.nx * W, by = p1.ny * H;
      const text = m.text || "Note";
      // LAST-CHANCE RESCUE — callout reads like a plan callout, not a UI
      // widget. Tight translucent-white background only where the label
      // sits (so the leader text stays legible) + colored stroke. No black
      // box. Fatter dashed leader, glove-sized anchor.
      const padX = 8 / vs;
      const padY = 5 / vs;
      const fontSize = 16 / vs;
      const charW = fontSize * 0.62;
      const w = Math.max(60 / vs, text.length * charW + padX * 2);
      const h = fontSize + padY * 2;
      return (
        <g opacity={opacity}>
          <line x1={ax} y1={ay} x2={bx} y2={by} stroke={stroke} strokeWidth={sw * 0.7} strokeLinecap="round" strokeDasharray={`${8 / vs} ${5 / vs}`} vectorEffect="non-scaling-stroke" />
          <circle cx={ax} cy={ay} r={6 / vs} fill={stroke} stroke="#fff" strokeWidth={1.5 / vs} />
          <rect
            x={bx - w / 2} y={by - h / 2}
            width={w} height={h}
            rx={3 / vs}
            fill="rgba(255,255,255,0.92)"
            stroke={stroke}
            strokeWidth={1.5 / vs}
          />
          <text
            x={bx} y={by + fontSize * 0.35}
            textAnchor="middle"
            fontFamily="ui-monospace,Menlo,monospace"
            fontSize={fontSize}
            fontWeight="700"
            fill={stroke}
          >
            {text}
          </text>
        </g>
      );
    }
    default: return null;
  }
}

/* ------------------------------------------------------------------ */
/* MarkupReadBanner — R-06 (independent recheck, 2026-10-01).          */
/* The first real, visible consumer of forgedops:read-degraded for    */
/* markups: an "access denied" or "showing last saved" state with a   */
/* retry action, instead of a silent/empty list that looks clean.     */
/* ------------------------------------------------------------------ */
export function MarkupReadBanner({ readState, onRetry }) {
  if (!readState) return null;
  const forbidden = readState === "forbidden";
  return (
    <div
      className={`fixed z-[70] left-1/2 -translate-x-1/2 top-[64px] flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg font-mono text-[11px] uppercase tracking-wider font-bold ${
        forbidden ? "bg-[var(--fop-danger)] text-white" : "bg-[var(--fop-cta-bg)] text-black"
      }`}
      data-testid="markup-read-banner"
      data-read-state={readState}
    >
      {forbidden ? <ShieldAlert size={14} /> : <AlertTriangle size={14} />}
      {forbidden
        ? "Access denied — you can't view markups on this sheet"
        : "Couldn't refresh markups — showing last saved version"}
      <button
        onClick={onRetry}
        className="ml-1 flex items-center gap-1 px-2 py-0.5 rounded bg-black/20 hover:bg-black/30"
        data-testid="markup-read-retry"
      >
        <RefreshCw size={11} /> Retry
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* MarkupControls — toolbar UI                                         */
/* ------------------------------------------------------------------ */
export default function MarkupControls({ active, tool, color, onSetTool, onSetColor, onClearAll, onUndo, count, onClose, inProgress }) {
  if (!active) return null;
  // Iter FIELD-UX-1 — plain-English progress prompt.
  // Replaces the silent "Pick a tool / N placed" line with a step-by-
  // step instruction so the operator always knows what the next tap does.
  const promptFor = (tid) => {
    if (!tid) return ["Markups", count > 0 ? `${count} placed · pick a tool` : "Pick a tool"];
    if (tid === "freehand") return ["Freehand", "Press and drag to draw"];
    if (tid === "text") return ["Text", "Tap where the note should go"];
    if (tid === "callout") {
      if (!inProgress) return ["Callout", "Tap the thing you're calling out"];
      return ["Callout", "Tap where the label should sit"];
    }
    // Two-tap tools (arrow / rect / circle / cloud / highlight)
    const labels = { arrow: "Arrow", rectangle: "Rectangle", circle: "Circle", cloud: "Cloud", highlight: "Highlight" };
    const lbl = labels[tid] || "Tool";
    if (!inProgress) return [lbl, "Tap start point"];
    return [lbl, "Tap or drag to endpoint"];
  };
  const [promptTitle, promptHint] = promptFor(tool);
  return (
    <div
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[55] rounded-lg shadow-2xl border border-[var(--fop-border-hi)] bg-[var(--fop-surface)] flex items-stretch overflow-hidden"
      data-testid="markup-controls"
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 px-4 py-2 border-r border-[var(--fop-border)] min-w-[200px]">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--fop-text-2)]">
            {promptTitle}
          </div>
          <div className="text-[16px] text-[var(--fop-text)] font-mono font-bold leading-tight" data-testid="markup-prompt">
            {promptHint}
          </div>
        </div>
      </div>
      {TOOLS.map((t) => (
        <button
          key={t.id}
          onClick={() => onSetTool(tool === t.id ? null : t.id)}
          className={`px-2.5 py-2 border-r border-[var(--fop-border)] flex items-center gap-1 ${
            tool === t.id ? "bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]" : "text-[var(--fop-text-2)] hover:text-[var(--fop-text)]"
          }`}
          title={t.label}
          data-testid={`markup-tool-${t.id}`}
        >
          <t.icon size={12} />
        </button>
      ))}
      {/* Iter EXP-4 — Foreman palette: six glove-sized named swatches. */}
      <div className="flex items-stretch border-r border-[var(--fop-border)]" data-testid="markup-palette">
        {COLORS.map((c) => (
          <button
            key={c.hex}
            type="button"
            onClick={() => onSetColor(c.hex)}
            className={`w-9 h-full flex items-center justify-center transition-colors ${
              color === c.hex ? "bg-[var(--fop-surface-2)]" : "hover:bg-[var(--fop-surface-2)]/60"
            }`}
            data-testid={`markup-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            data-color-name={c.name}
            title={`${c.name} · ${c.use}`}
            aria-label={c.name}
          >
            <span
              className={`block w-[22px] h-[22px] rounded-full ${
                color === c.hex ? "ring-2 ring-white shadow-md" : "ring-1 ring-black/30"
              }`}
              style={{ background: c.hex }}
            />
          </button>
        ))}
      </div>
      <button
        onClick={onUndo}
        disabled={count === 0 || !onUndo}
        className="px-2.5 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-primary)] disabled:opacity-40 border-r border-[var(--fop-border)] flex items-center gap-1"
        data-testid="markup-undo"
        title="Undo last markup"
        aria-label="Undo last markup"
      >
        <Undo2 size={12} /> Undo
      </button>
      <button
        onClick={onClearAll}
        disabled={count === 0}
        className="px-2.5 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--st-escalated)] disabled:opacity-40 border-r border-[var(--fop-border)] flex items-center gap-1"
        data-testid="markup-clear-all"
      >
        <Eraser size={11} /> Clear
      </button>
      <button
        onClick={onClose}
        className="px-3 py-2 text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
        data-testid="markup-close"
        aria-label="Close"
      >
        <X size={13} />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* useMarkupInteraction — placement state machine for the active tool  */
/* Returns the canvas event hooks SheetViewer needs to bind.           */
/* ------------------------------------------------------------------ */
export function useMarkupInteraction({ tool, color, add }) {
  const [inProgress, setInProgress] = useState(null); // { type, points, color }
  const [textPending, setTextPending] = useState(null); // { nx, ny } for text/callout

  const reset = () => { setInProgress(null); setTextPending(null); };

  // Click-based tools (taps>=1, non-freehand).
  const onClick = (nx, ny) => {
    if (!tool) return false;
    const def = TOOLS.find((t) => t.id === tool);
    if (!def) return false;
    if (tool === "freehand") return false; // handled by drag
    if (def.taps === 1) {
      // text — open prompt.
      if (tool === "text") {
        setTextPending({ nx, ny });
        return true;
      }
    }
    if (def.taps === 2) {
      if (!inProgress) {
        setInProgress({ type: tool, color, points: [{ nx, ny }] });
        return true;
      } else {
        // For callout — second point is label position → prompt for text.
        if (tool === "callout") {
          setTextPending({ nx, ny, anchor: inProgress.points[0] });
          setInProgress(null);
          return true;
        }
        // Iter 178.8 — commit with EXACTLY two points: the recorded start
        // (points[0]) and the tap point. Previously we spread
        // inProgress.points which had been mutated by `onHover` to
        // include a phantom preview point, so we shipped 3 points to the
        // API and it 400'd with "rectangle requires exactly 2 points".
        // Silent no-op from the operator's perspective.
        add({ type: tool, color, points: [inProgress.points[0], { nx, ny }] });
        setInProgress(null);
        return true;
      }
    }
    return false;
  };

  const onHover = (nx, ny) => {
    if (!tool || !inProgress) return;
    setInProgress((prev) => prev ? { ...prev, points: [prev.points[0], { nx, ny }] } : prev);
  };

  // Freehand drag — capture stream of points.
  const freehandRef = useRef(null);
  const onFreehandStart = (nx, ny) => {
    if (tool !== "freehand") return false;
    freehandRef.current = { type: "freehand", color, points: [{ nx, ny }] };
    setInProgress(freehandRef.current);
    return true;
  };
  const onFreehandMove = (nx, ny) => {
    if (!freehandRef.current) return;
    const last = freehandRef.current.points[freehandRef.current.points.length - 1];
    if (last && Math.hypot(nx - last.nx, ny - last.ny) < 0.001) return;
    freehandRef.current.points.push({ nx, ny });
    setInProgress({ ...freehandRef.current });
  };
  const onFreehandEnd = () => {
    if (!freehandRef.current) return;
    if (freehandRef.current.points.length >= 2) add(freehandRef.current);
    freehandRef.current = null;
    setInProgress(null);
  };

  const submitText = (text) => {
    if (!textPending) return;
    if (textPending.anchor) {
      // Callout
      add({
        type: "callout", color,
        points: [textPending.anchor, { nx: textPending.nx, ny: textPending.ny }],
        text: text.trim() || "Note",
      });
    } else {
      add({ type: "text", color, points: [{ nx: textPending.nx, ny: textPending.ny }], text: text.trim() || "Note" });
    }
    setTextPending(null);
  };
  const cancelText = () => setTextPending(null);

  return {
    inProgress, textPending,
    onClick, onHover, reset,
    onFreehandStart, onFreehandMove, onFreehandEnd,
    submitText, cancelText,
  };
}

/* ------------------------------------------------------------------ */
/* TextEntryPopover — small inline prompt for text / callout content   */
/*  Iter 83.2C-2 — supports @user mentions via MentionInput.            */
/* ------------------------------------------------------------------ */
import { MentionInput } from "@/components/MentionAutocomplete";

export function TextEntryPopover({ open, defaultText = "", onSubmit, onCancel }) {
  const [val, setVal] = useState(defaultText);
  useEffect(() => { setVal(defaultText); }, [defaultText, open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center" onClick={onCancel} data-testid="markup-text-entry">
      <div
        className="bg-[var(--fop-surface)] border border-[var(--fop-primary)] rounded-lg shadow-2xl p-3 w-[320px] space-y-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--fop-primary)]">
          Note text · @-mention to notify a teammate
        </div>
        <MentionInput
          as="input"
          autoFocus
          value={val}
          onChange={setVal}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit(val);
            if (e.key === "Escape") onCancel();
          }}
          placeholder="e.g. CHECK SUBGRADE  @superintendent"
          className="w-full text-sm py-1.5 px-2 font-mono"
          data-testid="markup-text-input"
        />
        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onCancel} className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="markup-text-cancel">
            Cancel
          </button>
          <button onClick={() => onSubmit(val)} className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded bg-[var(--fop-primary)] text-black" data-testid="markup-text-save">
            Place
          </button>
        </div>
      </div>
    </div>
  );
}
